import { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Layout } from '@/components/Layout';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import type { ClickUpSettings } from '@/types';

const DEFAULT_SETTINGS: ClickUpSettings = {
  sprintListId: '',
  crmListId: '',
};

export function AdminClickUpSettingsPage() {
  const [settings, setSettings] = useState<ClickUpSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    getDoc(doc(db, 'settings', 'clickup'))
      .then(snap => {
        if (snap.exists()) setSettings({ ...DEFAULT_SETTINGS, ...(snap.data() as ClickUpSettings) });
      })
      .catch(() => setError('Failed to load settings'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      await setDoc(doc(db, 'settings', 'clickup'), settings, { merge: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError('Failed to save settings. Ensure you are an admin.');
    } finally {
      setSaving(false);
    }
  };

  const field = (
    label: string,
    key: keyof ClickUpSettings,
    placeholder: string,
    hint?: string
  ) => (
    <div className="field-group">
      <label className="field-label">{label}</label>
      <input
        type="text"
        className="terminal-input"
        placeholder={placeholder}
        value={settings[key] as string}
        onChange={e => setSettings(prev => ({ ...prev, [key]: e.target.value }))}
        disabled={loading || saving}
      />
      {hint && (
        <span style={{ fontSize: '10px', color: 'var(--muted)', letterSpacing: '0.06em', marginTop: '2px' }}>
          {hint}
        </span>
      )}
    </div>
  );

  return (
    <ProtectedRoute requireAdmin>
      <Layout>
        <div style={{ padding: '24px 0' }}>

          {/* INTRO — native tools note */}
          <div className="terminal-card">
            <div className="card-header">
              <span className="card-title">ClickUp Ticket Sync</span>
              <div className="card-header-right">
                <span className="stat-chip">ADMIN <strong>SETTINGS</strong></span>
              </div>
            </div>
            <div className="card-body">
              <p style={{ color: 'var(--muted)', fontSize: '11px', letterSpacing: '0.04em', margin: 0, lineHeight: 1.6 }}>
                CRM, Calendar, Notes, and Sprint are now native GRID tools — no ClickUp embed needed.
                These settings only control the optional <strong style={{ color: 'var(--text-2)' }}>ticket → ClickUp task</strong> sync below.
              </p>
            </div>
          </div>

          {/* LIST IDS FOR API SYNC */}
          <div className="terminal-card">
            <div className="card-header">
              <span className="card-title">ClickUp API Sync</span>
            </div>
            <div className="card-body">
              <p style={{ color: 'var(--muted)', fontSize: '10px', letterSpacing: '0.06em', margin: 0 }}>
                List IDs are used to auto-create tasks when tickets and affiliates are submitted.
                How to find a list ID: in ClickUp, right-click the list → copy link → the number at the end is the list ID
              </p>

              {field(
                'Sprint List ID',
                'sprintListId',
                '123456789',
                'New support tickets are created as tasks in this list'
              )}
              {field(
                'CRM List ID',
                'crmListId',
                '987654321',
                'New affiliate signups are created as leads in this list'
              )}
            </div>
          </div>

          {/* FUNCTIONS CONFIG INSTRUCTIONS */}
          <div className="terminal-card">
            <div className="card-header">
              <span className="card-title">API Token Setup</span>
            </div>
            <div className="card-body">
              <p style={{ color: 'var(--muted)', fontSize: '10px', letterSpacing: '0.06em', margin: 0 }}>
                The ClickUp API token and webhook secret are stored securely in Firebase Functions config —
                not in this form. Run these commands in your terminal to configure them:
              </p>
              <div style={{
                background: 'var(--surface-strong)',
                border: '1px solid var(--border)',
                padding: '14px 16px',
                fontFamily: 'Consolas, monospace',
                fontSize: '10px',
                color: 'var(--gold)',
                letterSpacing: '0.04em',
                lineHeight: '1.8',
              }}>
                <div>firebase functions:config:set clickup.api_token="pk_YOUR_TOKEN_HERE"</div>
                <div>firebase functions:config:set clickup.webhook_secret="YOUR_WEBHOOK_SECRET"</div>
                <div>firebase deploy --only functions</div>
              </div>
              <p style={{ color: 'var(--muted)', fontSize: '10px', letterSpacing: '0.06em', margin: 0 }}>
                Get your API token: ClickUp → avatar → settings → apps → API token
              </p>
            </div>
          </div>

          {/* WEBHOOK SETUP INSTRUCTIONS */}
          <div className="terminal-card">
            <div className="card-header">
              <span className="card-title">Webhook Setup</span>
            </div>
            <div className="card-body">
              <p style={{ color: 'var(--muted)', fontSize: '10px', letterSpacing: '0.06em', margin: 0 }}>
                To receive status updates from ClickUp back into GRID Support, register a webhook in ClickUp:
              </p>
              <ol style={{ color: 'var(--muted)', fontSize: '10px', letterSpacing: '0.06em', paddingLeft: '20px', lineHeight: '2' }}>
                <li>ClickUp → workspace settings → integrations → webhooks</li>
                <li>Click "Add webhook"</li>
                <li>Endpoint URL: your Firebase function URL (e.g. https://[region]-[project].cloudfunctions.net/clickupWebhook)</li>
                <li>Events: select "Task status updated"</li>
                <li>Copy the webhook secret and set it via the terminal command above</li>
              </ol>
              <p style={{ color: 'var(--muted)', fontSize: '10px', letterSpacing: '0.06em', margin: 0 }}>
                Default status map: "To do" → OPEN | "In progress" → IN_PROGRESS | "Complete" → RESOLVED | "Closed" → CLOSED
              </p>
            </div>
          </div>

          {/* SAVE */}
          {error && (
            <div style={{ padding: '12px 16px', border: '1px solid var(--danger)', color: 'var(--danger)', fontSize: '11px', letterSpacing: '0.08em', marginBottom: '16px' }}>
              Error: {error}
            </div>
          )}
          {saved && (
            <div style={{ padding: '12px 16px', border: '1px solid var(--success)', color: 'var(--success)', fontSize: '11px', letterSpacing: '0.08em', marginBottom: '16px' }}>
              Settings saved successfully
            </div>
          )}
          <div className="btn-row">
            <button className="action-btn large" onClick={handleSave} disabled={saving || loading}>
              {saving ? 'Saving…' : 'Save Settings'}
            </button>
          </div>

        </div>
      </Layout>
    </ProtectedRoute>
  );
}
